import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useForm } from 'react-hook-form';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { User } from '@/types';
import { Copy, RefreshCw, Eye, EyeOff } from 'lucide-react';
import { generateSecurePassword, copyToClipboard } from '@/utils/passwordGenerator';

interface UserFormProps {
  isOpen: boolean;
  onClose: () => void;
  user?: User | null;
  districtId?: string;
  allowDistrictChange?: boolean;
}

interface UserFormData {
  first_name: string;
  last_name: string;
  email: string;
  username?: string;
  password?: string;
  employee_id: string;
  phone?: string;
  department?: string;
  position_title?: string;
  job_type?: string;
  school_name?: string;
  hire_date?: string;
  principal_name?: string;
  grade?: string;
  employment_status: string;
  supervisor_name?: string;
  emergency_contact?: string;
  emergency_phone?: string;
  required_pd_hours?: number;
  district_id?: string;
}

export default function UserForm({ isOpen, onClose, user, districtId, allowDistrictChange = false }: UserFormProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [districts, setDistricts] = useState<Array<{ id: string; name: string }>>([]);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors }
  } = useForm<UserFormData>();

  useEffect(() => {
    // Load districts if admin needs to change district assignment
    if (allowDistrictChange && isOpen) {
      loadDistricts();
    }

    if (user && isOpen) {
      // Pre-populate form with user data
      setValue('first_name', user.full_name?.split(' ')[0] || '');
      setValue('last_name', user.full_name?.split(' ').slice(1).join(' ') || '');
      setValue('email', user.email || '');
      setValue('employee_id', user.employee_id || '');
      setValue('phone', user.phone || '');
      setValue('department', user.department || '');
      setValue('position_title', user.job_title || '');
      setValue('job_type', user.job_type || '');
      setValue('school_name', user.school_name || '');
      setValue('hire_date', user.hire_date || '');
      setValue('principal_name', user.principal_name || '');
      setValue('grade', user.grade || '');
      setValue('employment_status', user.employment_status || 'active');
      setValue('supervisor_name', user.supervisor_name || '');
      setValue('emergency_contact', user.emergency_contact || '');
      setValue('emergency_phone', user.emergency_phone || '');
      setValue('required_pd_hours', user.required_pd_hours || 40);
      setValue('district_id', user.district_id || districtId || '');
    } else if (!user && isOpen) {
      // Reset form for new user
      reset();
      setValue('employment_status', 'active');
      setValue('required_pd_hours', 40);
      setValue('district_id', districtId || '');
      setGeneratedPassword('');
      setShowPassword(false);
    }
  }, [user, isOpen, setValue, reset, districtId, allowDistrictChange]);

  const handleGeneratePassword = () => {
    const { password } = generateSecurePassword();
    setGeneratedPassword(password);
    setValue('password', password);
    setShowPassword(true);
    toast({
      title: "Password Generated",
      description: "A secure password has been generated. Make sure to save it!",
    });
  };

  const handleCopyPassword = async () => {
    const passwordToCopy = generatedPassword || '';
    const success = await copyToClipboard(passwordToCopy);
    if (success) {
      toast({
        title: "Copied!",
        description: "Password copied to clipboard",
      });
    } else {
      toast({
        title: "Error",
        description: "Failed to copy password",
        variant: "destructive"
      });
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setGeneratedPassword(newPassword);
    setValue('password', newPassword);
  };

  const loadDistricts = async () => {
    try {
      const { data, error } = await supabase
        .from('districts')
        .select('id, name')
        .order('name');
      
      if (error) throw error;
      setDistricts(data || []);
    } catch (error) {
      console.error('Error loading districts:', error);
    }
  };

  const onSubmit = async (data: UserFormData) => {
    setLoading(true);
    try {
      const profileData = {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        employee_id: data.employee_id,
        phone: data.phone,
        department: data.department,
        position_title: data.position_title,
        job_type: data.job_type,
        school_name: data.school_name,
        hire_date: data.hire_date || null,
        principal_name: data.principal_name,
        grade: data.grade,
        employment_status: data.employment_status,
        supervisor_name: data.supervisor_name,
        emergency_contact: data.emergency_contact,
        emergency_phone: data.emergency_phone,
        required_pd_hours: data.required_pd_hours || 40,
        district_id: data.district_id || districtId,
        updated_at: new Date().toISOString()
      };

      if (user) {
        // Update existing user
        const { error } = await supabase
          .from('profiles')
          .update(profileData)
          .eq('id', user.id);

        if (error) throw error;
        
        toast({
          title: "Success",
          description: "User updated successfully",
        });
      } else {
        // Create new user via edge function
        const { data: session } = await supabase.auth.getSession();
        if (!session.session) {
          toast({
            title: "Error",
            description: "You must be logged in to create users",
            variant: "destructive"
          });
          return;
        }

        const requestBody = {
          email: data.email,
          username: data.username || data.email,
          password: data.password,
          first_name: data.first_name,
          last_name: data.last_name,
          employee_id: data.employee_id,
          district_id: data.district_id || districtId,
          phone: data.phone,
          department: data.department,
          position_title: data.position_title,
          job_type: data.job_type,
          school_name: data.school_name,
          hire_date: data.hire_date,
          principal_name: data.principal_name,
          grade: data.grade,
          employment_status: data.employment_status,
          supervisor_name: data.supervisor_name,
          emergency_contact: data.emergency_contact,
          emergency_phone: data.emergency_phone,
          required_pd_hours: data.required_pd_hours || 40
        };

        console.log('Creating user with district_id:', requestBody.district_id);

        const { data: result, error: createError } = await supabase.functions.invoke('create-user', {
          body: requestBody
        });

        if (createError) {
          console.error('Create user error:', createError);
          throw createError;
        }

        if (!result?.success) {
          console.error('Create user failed:', result);
          throw new Error(result?.error || 'Failed to create user');
        }

        console.log('User created successfully:', result);
        
        const passwordMessage = data.password 
          ? `User created with provided password.`
          : `User created successfully. Temporary password: ${result.temp_password}`;
        
        toast({
          title: "Success",
          description: passwordMessage,
          duration: 10000,
        });
      }

      onClose();
    } catch (error) {
      console.error('Error saving user:', error);
      toast({
        title: "Error",
        description: "Failed to save user. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {user ? 'Edit User' : 'Add New User'}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">First Name *</Label>
                <Input
                  id="first_name"
                  {...register('first_name', { required: 'First name is required' })}
                  className={errors.first_name ? 'border-red-500' : ''}
                />
                {errors.first_name && (
                  <p className="text-sm text-red-500">{errors.first_name.message}</p>
                )}
              </div>
              
              <div>
                <Label htmlFor="last_name">Last Name *</Label>
                <Input
                  id="last_name"
                  {...register('last_name', { required: 'Last name is required' })}
                  className={errors.last_name ? 'border-red-500' : ''}
                />
                {errors.last_name && (
                  <p className="text-sm text-red-500">{errors.last_name.message}</p>
                )}
              </div>
              
               <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  {...register('email', { 
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                  className={errors.email ? 'border-red-500' : ''}
                />
                {errors.email && (
                  <p className="text-sm text-red-500">{errors.email.message}</p>
                )}
              </div>

              {!user && (
                <>
                  <div>
                    <Label htmlFor="username">Username (Optional)</Label>
                    <Input
                      id="username"
                      {...register('username')}
                      placeholder="Defaults to email if not provided"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Used for login. If blank, email will be used.
                    </p>
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="password">Password (Optional)</Label>
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Input
                            id="password"
                            type={showPassword ? "text" : "password"}
                            placeholder="Auto-generated if not provided"
                            value={generatedPassword}
                            onChange={handlePasswordChange}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleGeneratePassword}
                          className="whitespace-nowrap"
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Generate
                        </Button>
                        {generatedPassword && (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={handleCopyPassword}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        If blank, a secure temporary password will be auto-generated
                      </p>
                    </div>
                  </div>
                </>
              )}

              
              <div>
                <Label htmlFor="employee_id">Employee ID *</Label>
                <Input
                  id="employee_id"
                  {...register('employee_id', { required: 'Employee ID is required' })}
                  className={errors.employee_id ? 'border-red-500' : ''}
                />
                {errors.employee_id && (
                  <p className="text-sm text-red-500">{errors.employee_id.message}</p>
                )}
              </div>
              
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  {...register('phone')}
                />
              </div>

              {allowDistrictChange && (
                <div>
                  <Label htmlFor="district_id">District Assignment</Label>
                  <Select 
                    onValueChange={(value) => setValue('district_id', value)}
                    defaultValue={user?.district_id || districtId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select district" />
                    </SelectTrigger>
                    <SelectContent>
                      {districts.map((district) => (
                        <SelectItem key={district.id} value={district.id}>
                          {district.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground mt-1">
                    Assign user to a district
                  </p>
                </div>
              )}
              
              <div>
                <Label htmlFor="employment_status">Employment Status</Label>
                <Select onValueChange={(value) => setValue('employment_status', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="terminated">Terminated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Job Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Job Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="department">Department</Label>
                <Input
                  id="department"
                  {...register('department')}
                />
              </div>
              
              <div>
                <Label htmlFor="position_title">Position Title</Label>
                <Input
                  id="position_title"
                  {...register('position_title')}
                />
              </div>
              
              <div>
                <Label htmlFor="job_type">Job Type</Label>
                <Select onValueChange={(value) => setValue('job_type', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select job type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="teacher">Teacher</SelectItem>
                    <SelectItem value="administrator">Administrator</SelectItem>
                    <SelectItem value="support_staff">Support Staff</SelectItem>
                    <SelectItem value="substitute">Substitute</SelectItem>
                    <SelectItem value="para_professional">Para Professional</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="hire_date">Hire Date</Label>
                <Input
                  id="hire_date"
                  type="date"
                  {...register('hire_date')}
                />
              </div>
              
              <div>
                <Label htmlFor="supervisor_name">Supervisor Name</Label>
                <Input
                  id="supervisor_name"
                  {...register('supervisor_name')}
                />
              </div>
            </div>
          </div>

          {/* School Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">School Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="school_name">School Name</Label>
                <Input
                  id="school_name"
                  {...register('school_name')}
                />
              </div>
              
              <div>
                <Label htmlFor="principal_name">Principal Name</Label>
                <Input
                  id="principal_name"
                  {...register('principal_name')}
                />
              </div>
              
              <div>
                <Label htmlFor="grade">Grade/Level</Label>
                <Input
                  id="grade"
                  {...register('grade')}
                  placeholder="e.g., K-5, 9-12, Adult Ed"
                />
              </div>
            </div>
          </div>

          {/* Emergency & PD Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Emergency Contact & Professional Development</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="emergency_contact">Emergency Contact</Label>
                <Input
                  id="emergency_contact"
                  {...register('emergency_contact')}
                />
              </div>
              
              <div>
                <Label htmlFor="emergency_phone">Emergency Phone</Label>
                <Input
                  id="emergency_phone"
                  {...register('emergency_phone')}
                />
              </div>
              
              <div>
                <Label htmlFor="required_pd_hours">Required PD Hours</Label>
                <Input
                  id="required_pd_hours"
                  type="number"
                  min="0"
                  {...register('required_pd_hours', { 
                    valueAsNumber: true,
                    min: { value: 0, message: 'Hours must be positive' }
                  })}
                />
                {errors.required_pd_hours && (
                  <p className="text-sm text-red-500">{errors.required_pd_hours.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-6 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : (user ? 'Update User' : 'Create User')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}